import React from 'react';

import { useRouter } from 'expo-router';
import {
  ButtonContainer,
  Container,
  StyledText,
  Title,
} from 'features/menu/components/menu-item-unavailable/styled';
import {
  IItemUnavailableData,
  MenuItemUnavailableProps,
} from 'features/menu/components/menu-item-unavailable/types';
import { navigateBackToMenu } from 'features/menu/utils/navigate-back-to-menu';
import { Pressable, StyleSheet } from 'react-native';

import ActionButton from 'components/action-button';
import { Typography } from 'components/ui';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { routes } from 'utils/routing';

export const MenuItemUnavailable: React.FC<React.PropsWithChildren<MenuItemUnavailableProps>> = ({
  isOffer,
  onChooseAnotherRestaurant,
}) => {
  const router = useRouter();

  /** Label is “back to menu” — always land on menu, not only dismiss the modal. */
  const backToMenu = () => navigateBackToMenu(router);
  const backToOffers = () => router.dismissTo(routes.offers);

  const menuItemUnavailableData: IItemUnavailableData = isOffer
    ? {
        title: 'offerNotAvailable',
        content: 'chooseDifferentOffer',
        buttonLabel: 'backToOffers',
        cta: backToOffers,
      }
    : {
        title: 'itemNotAvailable',
        content: 'chooseDifferentItem',
        buttonLabel: 'backToMenu',
        cta: backToMenu,
      };

  const { title, content, buttonLabel, cta } = menuItemUnavailableData;

  const titleMessage = t(title);

  useLogUserErrorMessage({
    message: titleMessage,
    severity: LogUserErrorSeverity.Recoverable,
    context: {
      moreInfo: 'menu-item-unavailable.tsx',
    },
  });

  return (
    <Container>
      <Title variant="headerOne" testID="menu-item-unavailable-title">
        {titleMessage}
      </Title>
      <StyledText variant="copyOne">
        {t('selectionNotAvailable')}
        {'\n'}
        {t(content)}
      </StyledText>
      <ButtonContainer>
        <ActionButton testID="menu-item-unavailable-cta" fullWidth onPress={cta}>
          {t(buttonLabel)}
        </ActionButton>
        {onChooseAnotherRestaurant ? (
          <Pressable
            accessibilityRole="button"
            onPress={onChooseAnotherRestaurant}
            style={styles.secondaryCta}
            testID="menu-item-unavailable-change-restaurant"
          >
            <Typography variant="copyTwo" color={primitive.$bbqBrown} style={styles.secondaryText}>
              <Typography variant="copyTwo" color={primitive.$bbqBrown} style={styles.tapHere}>
                {t('tapHere')}
              </Typography>
              {` ${t('toChooseAnotherRestaurant')}`}
            </Typography>
          </Pressable>
        ) : null}
      </ButtonContainer>
    </Container>
  );
};

const styles = StyleSheet.create({
  secondaryCta: {
    marginTop: spacing.$4,
    paddingVertical: spacing.$2,
  },
  secondaryText: {
    textAlign: 'center',
  },
  tapHere: {
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
});
